import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useRealtimeStore,
  type ControlQueueItem,
} from '@/stores/realtimeStore';
import { useThemeContext } from '@/context/ThemeContext';
import { getPalette } from '@/pages/(manager)/houses/utils/palette';
import { alpha } from '@/pages/(manager)/houses/utils/palette';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronUp,
  ChevronDown,
  X,
  Zap,
  AlertTriangle,
  GripHorizontal,
} from 'lucide-react';

const STATUS_ICON_SIZE = 14;

// ── 드래그 위치를 모듈 스코프에 저장 (컴포넌트 unmount/remount에도 유지) ──
let savedPos: { x: number; y: number } | null = null;

function useDrag() {
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    if (savedPos) return savedPos;
    // 초기 위치: 우상단
    return {
      x: Math.max(0, window.innerWidth - 400),
      y: 24,
    };
  });
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement | null>(null);

  // pos 변경 시 모듈 스코프에 저장
  useEffect(() => {
    savedPos = pos;
  }, [pos]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (!panelRef.current) return;
    dragging.current = true;
    const rect = panelRef.current.getBoundingClientRect();
    offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current || !panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    const rawX = e.clientX - offset.current.x;
    const rawY = e.clientY - offset.current.y;
    const x = Math.max(0, Math.min(rawX, window.innerWidth - rect.width));
    const y = Math.max(0, Math.min(rawY, window.innerHeight - rect.height));
    setPos({ x, y });
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  const resetToTopRight = useCallback(() => {
    requestAnimationFrame(() => {
      if (!panelRef.current) {
        const p = { x: Math.max(0, window.innerWidth - 400), y: 24 };
        setPos(p);
        return;
      }
      const rect = panelRef.current.getBoundingClientRect();
      const p = {
        x: Math.max(0, window.innerWidth - rect.width - 24),
        y: 24,
      };
      setPos(p);
    });
  }, []);

  const clampToViewport = useCallback(() => {
    requestAnimationFrame(() => {
      if (!panelRef.current) return;
      const rect = panelRef.current.getBoundingClientRect();
      setPos((prev) => ({
        x: Math.max(0, Math.min(prev.x, window.innerWidth - rect.width)),
        y: Math.max(0, Math.min(prev.y, window.innerHeight - rect.height)),
      }));
    });
  }, []);

  return {
    pos,
    panelRef,
    dragHandlers: { onPointerDown, onPointerMove, onPointerUp },
    resetToTopRight,
    clampToViewport,
  };
}

export function ControlQueuePanel() {
  const { isDark } = useThemeContext();
  const p = getPalette(isDark);

  // B2C 라우트에서는 자체 ControlStatusPanel을 사용하므로 숨김
  const isB2c = typeof window !== 'undefined' && window.location.pathname.startsWith('/b2c');
  const controlQueue = useRealtimeStore((s) => s.controlQueue);
  const rejectedItems = useRealtimeStore((s) => s.rejectedItems);
  const hasActive = controlQueue.size > 0;
  const items = useMemo(() => Array.from(controlQueue.values()), [controlQueue]);
  const expanded = useRealtimeStore((s) => s.panelExpanded);
  const setExpanded = useRealtimeStore((s) => s.setPanelExpanded);

  const [hidden, setHidden] = useState(false);
  const prevQueueSizeRef = useRef(controlQueue.size);
  const { pos, panelRef, dragHandlers, resetToTopRight, clampToViewport } = useDrag();

  const stats = useMemo(() => {
    const total = items.length;
    const success = items.filter((i) => i.status === 'success').length;
    const failed = items.filter((i) => i.status === 'failed').length;
    const done = success + failed;
    const pending = total - done;
    const rejectedCount = rejectedItems.length;
    return { total, done, success, failed, pending, rejectedCount };
  }, [items, rejectedItems]);

  const groups = useMemo(() => {
    const map = new Map<string, typeof items>();
    const singles: typeof items = [];
    items.forEach((item) => {
      if (item.batchId) {
        const group = map.get(item.batchId) || [];
        group.push(item);
        map.set(item.batchId, group);
      } else {
        singles.push(item);
      }
    });
    return { batches: map, singles };
  }, [items]);

  // 새 아이템 추가 시 hidden 해제
  useEffect(() => {
    if (controlQueue.size > prevQueueSizeRef.current) {
      setHidden(false);
    }
    prevQueueSizeRef.current = controlQueue.size;
  }, [controlQueue.size]);

  // 최초 등장 시에만 우상단 배치
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasActive && rejectedItems.length === 0) {
      hasInitialized.current = false;
      return;
    }
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      if (!savedPos) resetToTopRight();
    }
  }, [hasActive, rejectedItems.length, resetToTopRight]);

  // expanded 전환 시 화면 밖 보정
  useEffect(() => {
    if (hasInitialized.current) clampToViewport();
  }, [expanded, clampToViewport]);

  // B2C 라우트 또는 데이터 없으면 렌더링 안함
  if (isB2c || (!hasActive && rejectedItems.length === 0)) return null;

  // X 버튼으로 숨김 상태면 렌더링 안함 (데이터는 store에 유지)
  if (hidden) return null;

  // progress는 대기 중(pending) 목록만 기준 — rejected 제외
  const progressPercent = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  const pillLabel = (() => {
    if (stats.total === 0 && stats.rejectedCount > 0) return `제어 거부 ${stats.rejectedCount}건`;
    if (stats.pending > 0) return `제어 ${stats.total}건 진행중`;
    if (stats.failed > 0) return `제어 ${stats.total}건 완료 (${stats.failed}실패)`;
    return `제어 ${stats.total}건 완료`;
  })();

  const pillColor = (() => {
    if (stats.total === 0 && stats.rejectedCount > 0) return p.danger;
    if (stats.pending > 0) return p.accent;
    if (stats.failed > 0) return p.warning;
    return p.success;
  })();

  const handleHide = () => {
    setHidden(true);
  };

  const panel = (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        zIndex: 9999,
        fontFamily: '"DM Sans", "JetBrains Mono", sans-serif',
        userSelect: 'none',
      }}
    >
      {/* ── Pill ── */}
      {!expanded && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 12,
            border: `1px solid ${alpha(pillColor, 0.3)}`,
            background: p.surface,
            boxShadow: p.shadowLg,
            overflow: 'hidden',
          }}
        >
          <div
            {...dragHandlers}
            style={{
              padding: '10px 6px 10px 12px',
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              color: p.textMuted,
              touchAction: 'none',
            }}
          >
            <GripHorizontal size={14} />
          </div>
          <button
            onClick={() => setExpanded(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 16px 10px 4px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: p.text,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {stats.pending > 0 ? (
              <Loader2 size={16} style={{ color: pillColor, animation: 'spin 1s linear infinite' }} />
            ) : stats.failed > 0 ? (
              <AlertTriangle size={16} style={{ color: pillColor }} />
            ) : (
              <CheckCircle2 size={16} style={{ color: pillColor }} />
            )}
            <span>{pillLabel}</span>
            {stats.pending > 0 && (
              <span style={{ fontSize: 11, color: p.textMuted, fontWeight: 400 }}>
                {stats.done}/{stats.total}
              </span>
            )}
            <ChevronUp size={14} style={{ color: p.textMuted }} />
          </button>
          <button
            onClick={handleHide}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 12px 10px 0',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: p.textMuted,
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Expanded ── */}
      {expanded && (
        <div
          style={{
            width: 380,
            maxHeight: 420,
            borderRadius: 14,
            border: `1px solid ${p.border}`,
            background: p.surface,
            boxShadow: p.shadowLg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            {...dragHandlers}
            style={{
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${p.borderLight}`,
              cursor: 'grab',
              touchAction: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <GripHorizontal size={14} style={{ color: p.textMuted }} />
              <Zap size={16} style={{ color: p.accent }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: p.text }}>
                제어 현황
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                onClick={() => setExpanded(false)}
                onPointerDown={(e) => e.stopPropagation()}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, color: p.textMuted, display: 'flex' }}
              >
                <ChevronDown size={16} />
              </button>
              <button
                onClick={handleHide}
                onPointerDown={(e) => e.stopPropagation()}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, color: p.textMuted, display: 'flex' }}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ padding: '10px 16px 6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 11, color: p.textMuted }}>
              <span>{stats.pending > 0 ? `${stats.done}/${stats.total} 완료` : '전체 완료'}</span>
              <span>{progressPercent}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: alpha(p.textMuted, 0.15), overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPercent}%`, borderRadius: 3, background: stats.failed > 0 ? p.warning : p.accent, transition: 'width 0.3s ease' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: 11 }}>
              <span style={{ padding: '2px 8px', borderRadius: 6, background: alpha(p.success, 0.1), color: p.success, fontWeight: 600 }}>성공 {stats.success}</span>
              <span style={{ padding: '2px 8px', borderRadius: 6, background: alpha(p.danger, 0.1), color: stats.failed > 0 ? p.danger : p.textMuted, fontWeight: 600 }}>실패 {stats.failed}</span>
              {stats.pending > 0 && <span style={{ padding: '2px 8px', borderRadius: 6, background: alpha(p.textMuted, 0.1), color: p.textMuted, fontWeight: 600 }}>대기 {stats.pending}</span>}
              {stats.rejectedCount > 0 && <span style={{ padding: '2px 8px', borderRadius: 6, background: alpha(p.danger, 0.1), color: p.danger, fontWeight: 600 }}>거부 {stats.rejectedCount}</span>}
            </div>
          </div>

          {/* Item list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 12px' }}>
            {Array.from(groups.batches.entries()).map(([batchId, batchItems]) => {
              const batchDone = batchItems.filter((i) => i.status !== 'pending').length;
              const batchTotal = batchItems.length;
              const controlLabel = batchItems[0]?.controlType === 'DIMMING_LED' ? 'LED' : batchItems[0]?.controlType === 'DIMMING_FAN' ? 'FAN' : batchItems[0]?.controlType;
              return (
                <div key={batchId} style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: p.textMuted, marginBottom: 4 }}>
                    일괄 {controlLabel} ({batchDone}/{batchTotal})
                  </div>
                  {batchItems.map((item) => (
                    <QueueItemRow key={item.commandId} item={item} palette={p} />
                  ))}
                </div>
              );
            })}
            {groups.singles.map((item) => (
              <QueueItemRow key={item.commandId} item={item} palette={p} />
            ))}
            {rejectedItems.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: p.danger, marginBottom: 4 }}>거부됨</div>
                {rejectedItems.map((item, idx) => (
                  <div key={`rejected-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, marginBottom: 3, background: alpha(p.danger, 0.06), border: `1px solid ${alpha(p.danger, 0.15)}` }}>
                    <XCircle size={STATUS_ICON_SIZE} style={{ color: p.danger, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: p.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.nodeName}</div>
                      <div style={{ fontSize: 10, color: p.danger }}>{item.errorMessage}</div>
                    </div>
                    <span style={{ fontSize: 10, color: p.danger, fontWeight: 600, flexShrink: 0 }}>거부</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(panel, document.body);
}

function QueueItemRow({ item, palette: p }: { item: ControlQueueItem; palette: ReturnType<typeof getPalette> }) {
  const controlLabel = item.controlType === 'DIMMING_LED' ? 'LED'
    : item.controlType === 'DIMMING_FAN' ? 'FAN'
    : item.controlType === 'ACTIVATE_STREAM' ? 'STREAM'
    : item.controlType;

  const statusConfig = {
    pending: {
      icon: <Loader2 size={STATUS_ICON_SIZE} style={{ color: p.accent, animation: 'spin 1s linear infinite' }} />,
      label: '대기중', color: p.textMuted, bg: 'transparent', border: alpha(p.textMuted, 0.1),
    },
    success: {
      icon: <CheckCircle2 size={STATUS_ICON_SIZE} style={{ color: p.success }} />,
      label: '성공', color: p.success, bg: alpha(p.success, 0.04), border: alpha(p.success, 0.12),
    },
    failed: {
      icon: <XCircle size={STATUS_ICON_SIZE} style={{ color: p.danger }} />,
      label: item.result?.errorCode === 'CONTROL_TIMEOUT' ? '타임아웃' : '실패',
      color: p.danger, bg: alpha(p.danger, 0.04), border: alpha(p.danger, 0.12),
    },
  }[item.status];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, marginBottom: 3, background: statusConfig.bg, border: `1px solid ${statusConfig.border}`, transition: 'all 0.2s ease' }}>
      <div style={{ flexShrink: 0 }}>{statusConfig.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: p.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.nodeName}</div>
        {item.status === 'failed' && item.result?.errorMessage && (
          <div style={{ fontSize: 10, color: p.danger, marginTop: 1 }}>{item.result.errorMessage}</div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <span style={{ fontSize: 10, color: p.textMuted, fontWeight: 500 }}>{controlLabel}</span>
        <span style={{ fontSize: 10, color: statusConfig.color, fontWeight: 600 }}>{statusConfig.label}</span>
      </div>
    </div>
  );
}
